import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiChevronLeft, FiChevronRight, FiFilter } from 'react-icons/fi';
import { auditAPI, adminAPI } from '../lib/api';

const AuditLog = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data: itemsPerPageData } = useQuery({
    queryKey: ['settings', 'items_per_page'],
    queryFn: () => adminAPI.getPublicSetting('items_per_page'),
  });

  const itemsPerPage = parseInt(itemsPerPageData?.data?.setting_value || '20');

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log'],
    queryFn: () => auditAPI.getLogs({}),
  });

  // Debug logging
  console.log('[AuditLog] Query state:', {
    isLoading,
    error: error?.message,
    data,
    dataType: typeof data,
    dataKeys: data ? Object.keys(data) : null,
    dataData: data?.data,
    dataDataType: typeof data?.data
  });

  const getActionColor = (action) => {
    const colors = {
      CREATE: 'bg-green-100 text-green-700',
      UPDATE: 'bg-blue-100 text-blue-700',
      DELETE: 'bg-red-100 text-red-700',
      VIEW: 'bg-gray-100 text-gray-700',
      DOWNLOAD: 'bg-purple-100 text-purple-700',
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  // Get all logs
  const allLogs = data?.data?.data || [];

  // Apply filters
  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      if (actionFilter && log.action !== actionFilter) return false;
      if (resourceFilter && log.resource_type !== resourceFilter) return false;
      return true;
    });
  }, [allLogs, actionFilter, resourceFilter]);

  // Apply pagination
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLogs.slice(startIndex, endIndex);
  }, [filteredLogs, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [actionFilter, resourceFilter]);

  // Get unique values for filters
  const uniqueActions = useMemo(() => {
    return [...new Set(allLogs.map(log => log.action).filter(Boolean))];
  }, [allLogs]);

  const uniqueResourceTypes = useMemo(() => {
    return [...new Set(allLogs.map(log => log.resource_type).filter(Boolean))];
  }, [allLogs]);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-gray-600 mt-2">Track all system activities</p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FiFilter className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {(actionFilter || resourceFilter) && (
            <button
              onClick={() => {
                setActionFilter('');
                setResourceFilter('');
              }}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear all filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            {/* Action Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Actions</option>
                {uniqueActions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </select>
            </div>

            {/* Resource Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resource Type
              </label>
              <select
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Resources</option>
                {uniqueResourceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        {isLoading ? (
          <p className="text-gray-600">Loading...</p>
        ) : error ? (
          <div className="text-red-600">
            <p className="font-semibold">Error loading audit logs:</p>
            <p className="text-sm mt-1">{error.message}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Timestamp
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    User
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Action
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Resource
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-sm">
                          {log.user_profiles?.full_name || 'System'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {log.user_profiles?.email}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className="capitalize">{log.resource_type}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {log.ip_address === '::1' ? '127.0.0.1 (localhost)' : (log.ip_address || 'N/A')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLogs.length === 0 && (
              <p className="text-gray-500 text-center py-8">
                {actionFilter || resourceFilter ? 'No audit logs match the current filters' : 'No audit logs found'}
              </p>
            )}

            {/* Pagination Controls */}
            {filteredLogs.length > itemsPerPage && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * itemsPerPage + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredLogs.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">
                    {filteredLogs.length}
                  </span>{' '}
                  logs
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`px-3 py-1 border rounded-md text-sm font-medium ${
                        currentPage === idx + 1
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
